import { ApiError, requestJson, type ApiEnvelope } from '@shared/api/http';
import { uploadMedia, type MediaAsset } from '@domain/media/api';
import type {
  UserProfile,
  UserProfileSnapshot,
  UserProfileUpdateInput,
} from './types';

export class ProfileConflictError extends ApiError {
  constructor(public readonly current: UserProfileSnapshot, body: unknown) {
    super('Profile changed after it was loaded', 409, body);
    this.name = 'ProfileConflictError';
  }
}

function ensureSuccess<T>(body: ApiEnvelope<T>, fallbackMessage: string): ApiEnvelope<T> {
  if (body.status && body.status !== 'Success') {
    throw new ApiError(body.message ?? fallbackMessage, body.statusCode ?? 500, body);
  }
  return body;
}

function nullableString(value: unknown): string | null {
  return value == null ? null : String(value);
}

function toMediaLocator(raw: unknown) {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const assetId = String(value.assetId ?? '').trim();
  const url = String(value.url ?? '').trim();
  if (!assetId || !url) return null;
  return {
    assetId,
    provider: String(value.provider ?? 's3'),
    url,
    contentType: String(value.contentType ?? ''),
  };
}

function toProfile(raw: Record<string, unknown>): UserProfile {
  const userId = Number(raw.userId);
  const profileVersion = Number(raw.profileVersion);
  const displayName = String(raw.displayName ?? '').trim();
  if (!Number.isSafeInteger(userId) || userId <= 0 ||
      !Number.isSafeInteger(profileVersion) || profileVersion <= 0 ||
      !displayName) {
    throw new ApiError('Canonical profile payload is invalid', 500, raw);
  }
  return {
    userId,
    displayName,
    signature: nullableString(raw.signature),
    avatar: toMediaLocator(raw.avatar),
    homepageBackground: toMediaLocator(raw.homepageBackground),
    profileVersion,
    email: nullableString(raw.email),
    phone: nullableString(raw.phone),
  };
}

function requireProfileEtag(etag: string): string {
  const match = /^(?:W\/)?"profile-([1-9][0-9]*)"$/.exec(etag);
  if (!match) {
    throw new ApiError('Canonical profile response is missing a valid ETag', 500);
  }
  // The profile API uses the version as an If-Match concurrency token. Some
  // production proxies weaken the response ETag; send the canonical strong
  // form upstream because Business validates the profile version format.
  return `"profile-${match[1]}"`;
}

export async function uploadProfileImage(file: File, baseUrl: string, metadata?: Record<string, unknown>): Promise<MediaAsset> {
  return uploadMedia(baseUrl, file, { domain: 'profile', kind: 'image', metadata });
}

export async function fetchUserProfile(baseUrl?: string): Promise<UserProfileSnapshot> {
  let etag = '';
  const body = await requestJson<ApiEnvelope<Record<string, unknown>>>('/user/profile', {
    baseUrl,
    onResponse: (response) => {
      etag = response.headers.get('ETag') ?? '';
    },
  });
  const success = ensureSuccess(body, 'Failed to load user profile');
  return {
    profile: toProfile(success.data ?? {}),
    etag: requireProfileEtag(etag),
  };
}

export async function updateUserProfile(
  input: UserProfileUpdateInput,
  etag: string,
  baseUrl?: string,
): Promise<UserProfileSnapshot> {
  let responseEtag = '';
  let body: ApiEnvelope<Record<string, unknown>>;
  try {
    body = await requestJson<ApiEnvelope<Record<string, unknown>>>('/user/profile', {
      method: 'PATCH',
      baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'If-Match': requireProfileEtag(etag),
      },
      body: JSON.stringify(input),
      onResponse: (response) => {
        responseEtag = response.headers.get('ETag') ?? '';
      },
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 409 &&
        error.body && typeof error.body === 'object') {
      const envelope = error.body as ApiEnvelope<Record<string, unknown>>;
      throw new ProfileConflictError({
        profile: toProfile(envelope.data ?? {}),
        etag: requireProfileEtag(responseEtag),
      }, error.body);
    }
    throw error;
  }
  const success = ensureSuccess(body, 'Failed to update user profile');
  return {
    profile: toProfile(success.data ?? {}),
    etag: requireProfileEtag(responseEtag),
  };
}
